import React, { type ReactNode, useMemo } from 'react'
import clsx from 'clsx'
import { type ControllerFieldState, type FieldValues, useController, type UseControllerProps } from 'react-hook-form'
import { type ControllerRenderProps } from 'react-hook-form/dist/types/controller'
import { Box, TextField, useTheme } from '@mui/material'
import { isValidNumberString } from '../../../utils/string-utils'

interface ControlledTextFieldProps extends UseControllerProps {
  fieldId: string
  label: string
  value: string | number
}

function createStringTextField (fieldId: string, label: string, field: ControllerRenderProps<FieldValues, string>, fieldState: ControllerFieldState): ReactNode {
  return <TextField
    {...field}
    id={`${fieldId}-text-field`}
    key={`${fieldId}-text-field`}
    label={label}
    InputLabelProps={{
      shrink: true
    }}
    type='string'
    size='small'
    value={field.value ?? ''}
    className={clsx('fieldState', {
      dirty: fieldState?.isDirty,
      clean: !fieldState?.isDirty,
    })}
  />
}

function createNumberTextField (fieldId: string, label: string, field: ControllerRenderProps<FieldValues, string>, fieldState: ControllerFieldState): ReactNode {
  return <TextField
    {...field}
    id={`${fieldId}-text-field`}
    key={`${fieldId}-text-field`}
    label={label}
    InputLabelProps={{
      shrink: true
    }}
    type='number'
    size='small'
    InputProps={{
      inputProps: {},
    }}
    value={field.value ?? ''}
    onChange={(event) => {
      field.onChange(Number.parseFloat(event.target.value))
    }}
    className={clsx('fieldState', {
      dirty: fieldState?.isDirty,
      clean: !fieldState?.isDirty,
    })}
  />
}

const ControlledTextField: React.FC<ControlledTextFieldProps> = (props: ControlledTextFieldProps) => {
  const theme = useTheme()
  const { fieldId, label, value } = props

  const { field, fieldState } = useController(props)

  const node = useMemo(() => {
    if (typeof value === 'number' || isValidNumberString(value)) {
      return createNumberTextField(fieldId, label, field, fieldState)
    } else {
      return createStringTextField(fieldId, label, field, fieldState)
    }
  }, [field.value, fieldState.isDirty])

  return (
    <Box
      sx={{
        '& .fieldState.dirty': {
          backgroundColor: theme.palette.error.dark,
        },
        '& .fieldState.clean': {
          backgroundColor: 'transparent',
        },
      }}
    >
      {node}
    </Box>
  )
}

export default ControlledTextField
